import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import * as AT from "./actionTypes";
import { getFormById } from "./selectors/formSelectors";
import { getIsPrepaid } from "./selectors/customerSelectors";
import { FORM_ID, IdentificationMethod, VERIFICATION_TYPE } from "../../../../constants";
import { DeliveryAddressSuggestion, OfferingCode, RootState } from "../../../../models";
import { SearchResultDTO } from "../../../../models/external/ocmInterface";
import { Agent, VasOffering } from "../../../../models/state";
import { put as Ajax_put, get as Ajax_get, post as Ajax_post } from "../../app/Ajax";
import { API_URL_ADD_RETAILER_INFO } from "../../checkout/api";
import { addSubscriptionToCart, AddSubscriptionToCartResponse } from "../../checkout/state/actions/cartActions";
import { getNumberOfSubscriptionsInCart } from "../../checkout/state/selectors/cartSelectors";
import { actionTypes as RF_AT } from "../../rapidform";
import { VALIDATION_FIELD } from "../../rapidform/formValidation";
import {
    API_URL_IDP_AUTHORIZE,
    API_URL_PERFORM_CREDIT_CHECK,
    API_URL_CUSTOMER_LOOKUP,
    API_URL_RETAILER_SET_CUSTOMER_TYPE_AND_AGENT,
    API_URL_GET_ADDITIONAL_SERVICES,
    API_URL_SET_CONTACT_INFO,
    API_URL_SET_IDENTIFICATION,
    API_URL_RESET_IDENTIFICATION,
    API_URL_RETAILER_SUBMIT_ORDER,
    API_URL_GET_BANKID_RESULT,
    API_URL_GET_DRAFT_ORDERS,
    API_URL_GET_SIGNATURE_METHODS,
    API_URL_REQUEST_SIGN_DOCUMENT,
    API_URL_GET_SIGN_DOCUMENT_STATUS,
    API_URL_CONFIRM_MANUAL_SIGNATURE,
    API_URL_RETAILER_UPDATE_DELIVERY_ADDRESS,
    API_URL_RETAILER_CANCEL_AND_RESTART,
    API_URL_RETAILER_CANCEL_CONFLICTING_ORDER,
    API_URL_RETAILER_UPDATE_SUBSCRIPTION_PRODUCT_USER_EMAIL,
    API_URL_UPDATE_SELECTED_AGENT,
} from "../api";
import { SignatureMethodEnum } from "../types/enums";
import { IRetailerInfo } from "../../../../models/server/retailerInfo";

export const setValue = (field: string, value?: string | string[] | boolean) => ({
    type: AT.RETAILER_SET_CUSTOMER_INFO_VALUE,
    field,
    value,
});

export const resetCustomerInfo = () => ({
    type: AT.RETAILER_RESET_CUSTOMER_INFO,
});

export const addRetailerInfo = (
    dealerId: number,
    dealerCode: string,
    dealerName: string,
    chainName?: string,
    isManager?: boolean,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return (dispatch) => {
        const response = Ajax_put(
            {
                url: API_URL_ADD_RETAILER_INFO,
                data: {
                    chainName,
                    dealerId,
                    dealerCode,
                    dealerName,
                    isManager,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_ADD_INFO_TO_CART,
            payload: response,
        });

        return response;
    };
};

export const addSubscription =
    (
        offeringCode: OfferingCode,
        subscriptionId?: string,
    ): ThunkAction<Promise<AddSubscriptionToCartResponse>, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        return dispatch(
            addSubscriptionToCart(
                offeringCode,
                getNumberOfSubscriptionsInCart(state, offeringCode) + 1,
                subscriptionId,
            ),
        );
    };

export const idpAuthorize = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) => {
    dispatch({
        type: AT.RETAILER_IDP_AUTHORIZE,
        payload: Ajax_get({ url: API_URL_IDP_AUTHORIZE }, dispatch),
    });
};

export const setSelectedIdentificationMethod =
    (selectedIdentificationMethod: IdentificationMethod): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const identificationFields = {
            PASSPORT: "identity-passportNumber",
            DRIVERS_LICENSE: "identity-driversLicense",
            BANK_ID: "identity-bankId",
            NATIONAL_ID_CARD: "identity-nationalId",
        };

        // Remove all identification fields except the selected one
        Object.entries(identificationFields).forEach(([method, fieldName]) => {
            if (method !== selectedIdentificationMethod) {
                dispatch({
                    type: RF_AT.FORM_REMOVE_INPUT(FORM_ID.CUSTOMER_IDENTIFICATION),
                    payload: {
                        inputName: fieldName,
                    },
                });
            }
        });

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_IDENTIFICATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.CUSTOMER_IDENTIFICATION,
                inputName: `selectedIdentificationMethod`,
                inputValue: selectedIdentificationMethod,
                visible: false,
                started: false,
            },
        });
    };

export const getRetailCustomerBankIdResult =
    (): ThunkAction<
        Promise<{
            customer?: { birthdate?: string; firstName: string; lastName: string; ssn?: string };
            shouldPoll?: boolean;
            success: boolean;
        }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const response = Ajax_get<{
            customer?: { birthdate?: string; firstName: string; lastName: string; ssn?: string };
            shouldPoll?: boolean;
            success: boolean;
        }>(
            {
                url: API_URL_GET_BANKID_RESULT,
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_GET_BANKID_RESULT,
            payload: response,
        });

        response
            .then((result) => {
                const customer = result.customer;
                if (!result.success || !customer) {
                    return;
                }

                const firstName = customer.firstName || "";
                const lastName = customer.lastName || "";
                const ssn = customer.ssn || "";

                dispatch({
                    type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CREDIT_CHECK),
                    payload: {
                        errorMessage: undefined,
                        form: FORM_ID.CREDIT_CHECK,
                        inputName: "customerDetails-firstName",
                        inputValue: firstName,
                        visible: true,
                    },
                });
                dispatch({
                    type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CREDIT_CHECK),
                    payload: {
                        errorMessage: undefined,
                        form: FORM_ID.CREDIT_CHECK,
                        inputName: "customerDetails-lastName",
                        inputValue: lastName,
                        visible: true,
                    },
                });
                dispatch({
                    type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CREDIT_CHECK),
                    payload: {
                        errorMessage: undefined,
                        form: FORM_ID.CREDIT_CHECK,
                        inputName: "customerDetails-ssn",
                        inputValue: ssn,
                        visible: true,
                    },
                });
            })
            .catch(() => undefined);

        return response;
    };

export const cancelBankIdPolling = (): AnyAction => ({
    type: AT.RETAILER_CANCEL_BANKID_POLLING,
});
export const setRetailCustomer =
    (): ThunkAction<Promise<{ shouldPoll?: boolean; success: boolean }>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const { values } = getFormById(getState(), FORM_ID.CUSTOMER_IDENTIFICATION);
        const { identity, selectedIdentificationMethod } = values;
        const identification = { type: selectedIdentificationMethod, value: "" };

        switch (selectedIdentificationMethod) {
            case VERIFICATION_TYPE.NATIONAL_ID_CARD:
                identification.value = identity?.nationalId;
                break;
            case VERIFICATION_TYPE.DRIVERS_LICENSE:
                identification.value = identity?.driversLicense;
                break;
            case VERIFICATION_TYPE.PASSPORT:
                identification.value = identity?.passportNumber;
                break;
            case VERIFICATION_TYPE.BANK_ID:
                identification.value = identity?.bankId;
                break;
            default:
                break;
        }

        const response = Ajax_post<{ shouldPoll?: boolean; success: boolean; type?: string }>(
            {
                url: API_URL_SET_IDENTIFICATION,
                data: {
                    ...identification,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_SET_IDENTIFICATION,
            payload: response,
        });

        return response;
    };

export const setCustomerContactInfo =
    (): ThunkAction<Promise<{ success: boolean; email?: string; phone?: string }>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const { values } = getFormById(getState(), FORM_ID.CUSTOMER_CONTACT_INFO);
        const isPrepaid = getIsPrepaid(getState());
        const email = values?.details?.[VALIDATION_FIELD.EMAIL];
        const phone = isPrepaid
            ? ""
            : (values?.details?.[VALIDATION_FIELD.CONTACT_PHONE_NON_REQUIRED]?.replace(/\s/g, "") ?? "");

        const response = Ajax_post(
            {
                url: API_URL_SET_CONTACT_INFO,
                data: {
                    email,
                    phone,
                },
            },
            dispatch,
        ).then(() => ({
            success: true,
            email,
            phone,
        }));

        dispatch({
            type: AT.RETAILER_SET_CONTACT_INFO,
            payload: response,
        });

        return response;
    };

export const setCustomerTypeAndAgent =
    (
        customerType: string,
        agent: Agent | null,
    ): ThunkAction<Promise<{ success: boolean }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Ajax_put<{ success: boolean }>(
            {
                url: API_URL_RETAILER_SET_CUSTOMER_TYPE_AND_AGENT,
                data: {
                    customerType,
                    agent,
                },
            },
            dispatch,
        );

        const { success } = await response;
        return { success };
    };

export const updateSelectedAgent =
    (salesAgentId: string): ThunkAction<Promise<IRetailerInfo["selectedAgent"]>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const payload = Ajax_put<{ selectedAgent: IRetailerInfo["selectedAgent"] }>(
            { url: API_URL_UPDATE_SELECTED_AGENT(salesAgentId) },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_UPDATE_SELECTED_AGENT,
            payload: payload,
        });

        return payload.then((res) => res.selectedAgent);
    };

export const resetCustomerIdentification =
    (): ThunkAction<Promise<{ success: boolean }>, RootState, unknown, AnyAction> => async (dispatch) => {
        return Ajax_post<{ success: boolean }>({ url: API_URL_RESET_IDENTIFICATION, data: {} }, dispatch);
    };

/**
 * Perform customer lookup for existing customers
 * Only verifies PID and fetches customer data - does NOT perform credit check
 */
export const handleCustomerLookup =
    (
        firstName: string,
        lastName: string,
        nationalIdentificationNumber: string,
    ): ThunkAction<
        Promise<{ contactEmail?: string; contactPhone?: string; isProductUserLookup?: boolean; success: boolean }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const response = Ajax_post<{
            contactEmail?: string;
            contactPhone?: string;
            isProductUserLookup?: boolean;
            success: boolean;
        }>(
            {
                url: API_URL_CUSTOMER_LOOKUP,
                data: {
                    firstName,
                    lastName,
                    nationalIdentificationNumber,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_PERFORM_CREDIT_CHECK,
            payload: response,
        });

        response.then(({ contactEmail, contactPhone, isProductUserLookup }) => {
            if (isProductUserLookup) {
                return;
            }
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_CONTACT_INFO),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_CONTACT_INFO,
                    inputName: `details-${VALIDATION_FIELD.CONTACT_PHONE_NON_REQUIRED}`,
                    inputValue: contactPhone,
                    visible: false,
                },
            });
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_CONTACT_INFO),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_CONTACT_INFO,
                    inputName: `details-email`,
                    inputValue: contactEmail,
                    visible: false,
                },
            });
        });

        return response;
    };

export const handleCreditCheck =
    (
        forceCreditCheck = false,
        customerDetails?: {
            firstName: string;
            lastName: string;
            nationalIdentificationNumber: string;
            street?: string;
            postcode?: string;
            city?: string;
        },
    ): ThunkAction<
        Promise<{
            contactEmail?: string;
            contactPhone?: string;
            success: boolean;
            requireManuallyAddress?: boolean;
            street?: string;
        }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        // Backend will retrieve customer details from rapidOrder
        // No need to send them from frontend

        const data = customerDetails
            ? {
                  forceCreditCheck,
                  ...customerDetails,
              }
            : { forceCreditCheck };

        const response = Ajax_post<{
            contactEmail?: string;
            contactPhone?: string;
            success: boolean;
            requireManuallyAddress?: boolean;
            street?: string;
        }>(
            {
                url: API_URL_PERFORM_CREDIT_CHECK,
                data,
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_PERFORM_CREDIT_CHECK,
            payload: response,
        });

        response.then(({ contactEmail, contactPhone }) => {
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_CONTACT_INFO),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_CONTACT_INFO,
                    inputName: `details-${VALIDATION_FIELD.CONTACT_PHONE_NON_REQUIRED}`,
                    inputValue: contactPhone,
                    visible: false,
                },
            });
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_CONTACT_INFO),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_CONTACT_INFO,
                    inputName: `details-email`,
                    inputValue: contactEmail,
                    visible: false,
                },
            });
        });

        return response;
    };

export const fetchAdditionalServices =
    (
        stateKey: string,
        phoneNumber: string,
        memberId?: string,
    ): ThunkAction<Promise<{ products: VasOffering[]; addedProducts: VasOffering[] }>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const response = Ajax_post<{ products: VasOffering[]; addedProducts: VasOffering[] }>(
            {
                url: API_URL_GET_ADDITIONAL_SERVICES,
                data: {
                    memberId: memberId || "",
                    phoneNumber,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_FETCH_ADDITIONAL_SERVICES,
            payload: response,
            meta: { memberId: stateKey },
        });

        return response;
    };

export const submitOrder =
    (): ThunkAction<
        Promise<{ newOrderRef: string; orderRef: string; success: boolean }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const response = Ajax_post<{ newOrderRef: string; orderRef: string; success: boolean }>(
            {
                url: API_URL_RETAILER_SUBMIT_ORDER,
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_SUBMIT_ORDER,
            payload: response,
        });

        return response;
    };

export const cancelAndRestart = (): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => async (dispatch) => {
    const response = Ajax_post({ url: API_URL_RETAILER_CANCEL_AND_RESTART }, dispatch);

    dispatch({
        type: AT.RETAILER_CANCEL_AND_RESTART,
        payload: response,
    });

    return response;
};

export const cancelConflictingOrder =
    (orderId: number): ThunkAction<Promise<{ success: boolean }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Ajax_post<{ success: boolean }>(
            {
                url: API_URL_RETAILER_CANCEL_CONFLICTING_ORDER,
                data: { orderId },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_CANCEL_CONFLICTING_ORDER,
            payload: response,
        });

        return response;
    };

export const updateSubscriptionProductUserEmail =
    (
        subscriptionId: string,
        email: string,
    ): ThunkAction<Promise<{ success: boolean }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Ajax_post<{ success: boolean }>(
            {
                url: API_URL_RETAILER_UPDATE_SUBSCRIPTION_PRODUCT_USER_EMAIL,
                data: { subscriptionId, email },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_UPDATE_SUBSCRIPTION_PRODUCT_USER_EMAIL,
            payload: response,
        });

        return response;
    };

export const fetchDraftOrders =
    (): ThunkAction<Promise<SearchResultDTO>, RootState, unknown, AnyAction> => async (dispatch) => {
        const response = Ajax_get<SearchResultDTO>(
            {
                url: API_URL_GET_DRAFT_ORDERS(),
            },
            dispatch,
        );
        dispatch({
            type: AT.RETAILER_FETCH_DRAFT_ORDERS,
            payload: response,
        });

        return response;
    };

export const fetchSignatureMethods =
    (
        ownerPresent?: boolean,
    ): ThunkAction<
        Promise<{ signatureMethods: Array<{ signature_method: SignatureMethodEnum }> }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const url =
            ownerPresent !== undefined
                ? `${API_URL_GET_SIGNATURE_METHODS}?ownerPresent=${ownerPresent}`
                : API_URL_GET_SIGNATURE_METHODS;
        const response = Ajax_get<{ signatureMethods: Array<{ signature_method: SignatureMethodEnum }> }>(
            { url },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_GET_SIGNATURE_METHODS,
            payload: response,
        });

        return response;
    };

export const requestSignDocument =
    (
        signatureMethod?: string,
    ): ThunkAction<
        Promise<{ session_id?: string; status: string; documents?: Array<{ id: string; type: string }> }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const response = Ajax_post<{
            session_id?: string;
            status: string;
            documents?: Array<{ id: string; type: string }>;
        }>({ url: API_URL_REQUEST_SIGN_DOCUMENT, data: { signatureMethod } }, dispatch);

        dispatch({
            type: AT.RETAILER_REQUEST_SIGN_DOCUMENT,
            payload: response,
        });

        return response;
    };

export const getSignDocumentStatus =
    (): ThunkAction<Promise<{ orderRef?: string; status: string }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Ajax_get<{ orderRef?: string; status: string }>(
            { url: API_URL_GET_SIGN_DOCUMENT_STATUS },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_GET_SIGN_DOCUMENT_STATUS,
            payload: response,
        });

        return response;
    };

export const confirmManualSignature =
    (): ThunkAction<Promise<{ orderRef: string; status: string }>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response = Ajax_post<{ orderRef: string; status: string }>(
            { url: API_URL_CONFIRM_MANUAL_SIGNATURE },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_CONFIRM_MANUAL_SIGNATURE,
            payload: response,
        });

        return response;
    };

export const updateRetailerDeliveryAddress =
    (
        selectedAddress: DeliveryAddressSuggestion,
    ): ThunkAction<
        Promise<{ success: boolean; updatedAddress?: DeliveryAddressSuggestion }>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        return Ajax_post<{ success: boolean; updatedAddress?: DeliveryAddressSuggestion }>(
            {
                url: API_URL_RETAILER_UPDATE_DELIVERY_ADDRESS,
                data: { selectedAddress },
            },
            dispatch,
        );
    };
